import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import {
  Badge,
  Empty,
  Field,
  FormGrid,
  Modal,
  Notice,
  Page,
  Panel,
  Table,
  fmtDateTime,
  fmtNumber,
  fmtPhone,
} from '../components/ui'
import type {
  CampaignSummary,
  ConsentRecord,
  DncImport,
  ListSummary,
  Role,
  ScrubJob,
  SettingsMap,
} from '../types'

type Tab = 'scrub' | 'consent' | 'suppression'

export default function Compliance({ role }: { role: Role }) {
  const [tab, setTab] = useState<Tab>('scrub')
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  return (
    <Page
      title="Compliance"
      subtitle="Number intelligence, consent evidence, and suppression lists"
    >
      {notice && (
        <Notice kind={notice.kind} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <div className="tabs">
        {(['scrub', 'consent', 'suppression'] as Tab[]).map((t) => (
          <button key={t} className={tab === t ? 'tab on' : 'tab'} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'scrub' && <Scrub role={role} notify={setNotice} />}
      {tab === 'consent' && <Consent notify={setNotice} />}
      {tab === 'suppression' && <Suppression role={role} notify={setNotice} />}
    </Page>
  )
}

type Notify = (n: { kind: 'success' | 'error'; text: string } | null) => void

// ================================================================= scrub ====

function Scrub({ role, notify }: { role: Role; notify: Notify }) {
  const [settings, setSettings] = useState<SettingsMap | null>(null)
  const [jobs, setJobs] = useState<ScrubJob[]>([])
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [lists, setLists] = useState<ListSummary[]>([])
  const [campaignId, setCampaignId] = useState('')
  const [listId, setListId] = useState('')
  const [editing, setEditing] = useState(false)

  const load = () => {
    api.scrubJobs().then(setJobs).catch(() => setJobs([]))
    if (role === 'admin') api.settings().then(setSettings).catch(() => setSettings(null))
  }

  useEffect(() => {
    load()
    api.campaigns().then(setCampaigns).catch(() => setCampaigns([]))
    // Jobs move slowly; five seconds is enough to watch a long run advance.
    const t = setInterval(() => api.scrubJobs().then(setJobs).catch(() => undefined), 5000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!campaignId) { setLists([]); setListId(''); return }
    api.lists(campaignId).then(setLists).catch(() => setLists([]))
  }, [campaignId])

  const enabled = settings?.['scrub.enabled'] === true
  const keySet = (settings?.['scrub.api_key'] as { set?: boolean } | undefined)?.set === true

  const start = async () => {
    try {
      await api.startScrub({
        list_id: listId || undefined,
        campaign_id: !listId && campaignId ? campaignId : undefined,
      })
      notify({ kind: 'success', text: 'Scrub queued. It runs in the background.' })
      load()
    } catch (e) {
      notify({ kind: 'error', text: e instanceof ApiError ? e.message : 'Could not start' })
    }
  }

  const running = jobs.find((j) => j.status === 'running' || j.status === 'queued')

  return (
    <>
      <Panel
        title="Number intelligence"
        actions={
          role === 'admin' && (
            <button className="ghost" onClick={() => setEditing(true)}>
              Configure
            </button>
          )
        }
      >
        {role === 'admin' && settings && !enabled && (
          <Notice kind="warn">
            Scrubbing is switched off. Numbers are dialled without checking whether they are live,
            mobile, or flagged.
          </Notice>
        )}
        {role === 'admin' && settings && enabled && !keySet && (
          <Notice kind="error">
            Scrubbing is enabled but no API key has been entered, so no lookup can be made. The gate
            treats this as "unknown", not as "clean".
          </Notice>
        )}

        <p className="muted small">
          Each number is checked once and cached. The result tells the compliance gate whether the
          line is live, whether it is a mobile — which changes the consent rules — and whether the
          provider has it flagged.
        </p>

        <div className="filter-bar">
          <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">Choose a campaign</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={listId} onChange={(e) => setListId(e.target.value)} disabled={!campaignId}>
            <option value="">Whole campaign</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>{l.name} ({fmtNumber(l.total)})</option>
            ))}
          </select>
          <button className="primary" onClick={start} disabled={!campaignId || !!running}>
            {running ? 'A scrub is already running' : 'Scrub numbers'}
          </button>
        </div>
      </Panel>

      <Panel title="Scrub jobs">
        {jobs.length === 0 ? (
          <Empty message="No scrub jobs yet." />
        ) : (
          <Table head={['Started', 'List', 'Status', 'Progress', 'Fetched', 'Flagged', 'Failed', '']}>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td className="muted">{fmtDateTime(j.created_at)}</td>
                <td>{j.list ?? <span className="muted">whole campaign</span>}</td>
                <td>
                  <Badge
                    tone={
                      j.status === 'done' ? 'ok'
                      : j.status === 'failed' ? 'danger'
                      : j.status === 'running' ? 'live'
                      : 'muted'
                    }
                  >
                    {j.status}
                  </Badge>
                  {j.error && <div className="muted small">{j.error}</div>}
                </td>
                <td>
                  <div className="bar-cell">
                    <span
                      className="bar-fill"
                      style={{ width: `${j.total ? (j.processed / j.total) * 100 : 0}%` }}
                    />
                    <span className="bar-text">
                      {fmtNumber(j.processed)}/{fmtNumber(j.total)}
                    </span>
                  </div>
                </td>
                <td>{fmtNumber(j.fetched)}</td>
                <td className={j.flagged > 0 ? 'danger-text strong' : ''}>{fmtNumber(j.flagged)}</td>
                <td className="muted">{fmtNumber(j.failed)}</td>
                <td className="row-actions">
                  {(j.status === 'queued' || j.status === 'running') && (
                    <button
                      className="link danger-text"
                      onClick={async () => { await api.cancelScrub(j.id); load() }}
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {editing && settings && (
        <ScrubSettingsModal
          settings={settings}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); notify({ kind: 'success', text: 'Settings saved. The dialer picks up scrub changes on restart.' }) }}
          onError={(m) => notify({ kind: 'error', text: m })}
        />
      )}
    </>
  )
}

function ScrubSettingsModal({
  settings,
  onClose,
  onSaved,
  onError,
}: {
  settings: SettingsMap
  onClose: () => void
  onSaved: () => void
  onError: (m: string) => void
}) {
  const key = settings['scrub.api_key'] as { set?: boolean; hint?: string } | undefined
  const [form, setForm] = useState({
    'scrub.enabled': settings['scrub.enabled'] === true,
    'scrub.api_key': '',
    'scrub.cache_days': Number(settings['scrub.cache_days'] ?? 30),
    'scrub.fraud_threshold': Number(settings['scrub.fraud_threshold'] ?? 85),
    'scrub.block_disconnected': settings['scrub.block_disconnected'] === true,
    'scrub.block_dnc': settings['scrub.block_dnc'] === true,
    'compliance.require_consent_for_wireless':
      settings['compliance.require_consent_for_wireless'] === true,
  })
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await api.updateSettings(form)
      onSaved()
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Number intelligence settings" onClose={onClose} wide>
      <FormGrid>
        <Field
          label="IPQualityScore API key"
          hint={
            key?.set
              ? `A key ending ${key.hint} is stored. Leave blank to keep it.`
              : 'No key stored yet.'
          }
        >
          <input
            type="password"
            value={form['scrub.api_key']}
            onChange={(e) => setForm({ ...form, 'scrub.api_key': e.target.value })}
            placeholder={key?.set ? '••••••••' : 'Paste the key'}
          />
        </Field>
        <Field label="Re-check after (days)" hint="Line type rarely changes; disconnection does.">
          <input
            type="number"
            value={form['scrub.cache_days']}
            onChange={(e) => setForm({ ...form, 'scrub.cache_days': +e.target.value })}
          />
        </Field>
        <Field
          label="Block at risk score"
          hint="0–100. IPQS treats 85+ as high risk. Set 101 to never block on score."
        >
          <input
            type="number"
            min={0}
            max={101}
            value={form['scrub.fraud_threshold']}
            onChange={(e) => setForm({ ...form, 'scrub.fraud_threshold': +e.target.value })}
          />
        </Field>
      </FormGrid>

      <div className="check-row wrap">
        {([
          ['scrub.enabled', 'Check numbers before dialling'],
          ['scrub.block_dnc', 'Block numbers the provider reports as do-not-call'],
          ['scrub.block_disconnected', 'Block numbers that are not in service'],
          [
            'compliance.require_consent_for_wireless',
            'Require a consent record before an automated campaign dials a mobile',
          ],
        ] as const).map(([k, label]) => (
          <label key={k}>
            <input
              type="checkbox"
              checked={form[k] as boolean}
              onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
            />
            {label}
          </label>
        ))}
      </div>

      <Notice kind="info">
        A lookup that fails never allows a call it would otherwise have blocked — the gate falls
        back to whatever it already knew about the number.
      </Notice>

      <div className="modal-actions">
        <button className="ghost" onClick={onClose}>Cancel</button>
        <button className="primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  )
}

// =============================================================== consent ====

function Consent({ notify }: { notify: Notify }) {
  const [data, setData] = useState<{ total: number; records: ConsentRecord[] } | null>(null)
  const [q, setQ] = useState('')
  const [adding, setAdding] = useState(false)

  const load = () => api.consent({ q: q || undefined }).then(setData).catch(() => setData(null))
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  return (
    <>
      <Panel
        title="Consent records"
        actions={<button className="primary" onClick={() => setAdding(true)}>Record consent</button>}
      >
        <Notice kind="info">
          Written express consent is what permits an automated campaign to dial a mobile. A record
          that cannot say what the person was shown and where they agreed is not evidence — the
          disclosure wording and source are the whole point of keeping it.
        </Notice>

        <div className="filter-bar">
          <input
            className="grow"
            placeholder="Search by number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="muted">{data ? `${fmtNumber(data.total)} records` : ''}</span>
        </div>

        {!data || data.records.length === 0 ? (
          <Empty
            message="No consent records."
            hint="Automated campaigns will refuse to dial mobiles until consent exists for them."
          />
        ) : (
          <Table head={['Number', 'Type', 'Captured', 'Source', 'Expires', 'Status', '']}>
            {data.records.map((c) => (
              <tr key={c.id}>
                <td className="mono strong">{fmtPhone(c.phone_e164)}</td>
                <td><Badge tone="muted">{c.consent_type.replace('_', ' ')}</Badge></td>
                <td className="muted">{fmtDateTime(c.captured_at)}</td>
                <td>
                  {c.source}
                  {c.disclosure_text && (
                    <div className="muted small" title={c.disclosure_text}>
                      {c.disclosure_text.slice(0, 60)}
                      {c.disclosure_text.length > 60 ? '…' : ''}
                    </div>
                  )}
                </td>
                <td className="muted">{c.expires_at ? fmtDateTime(c.expires_at) : 'never'}</td>
                <td>
                  <Badge tone={c.live ? 'ok' : 'muted'}>
                    {c.revoked_at ? 'revoked' : c.live ? 'live' : 'expired'}
                  </Badge>
                </td>
                <td className="row-actions">
                  {c.live && (
                    <button
                      className="link danger-text"
                      onClick={async () => {
                        const reason = prompt('Why is this consent being withdrawn?')
                        if (reason === null) return
                        await api.revokeConsent(c.id, reason || 'revoked by operator')
                        load()
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {adding && (
        <ConsentModal
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); notify({ kind: 'success', text: 'Consent recorded.' }) }}
          onError={(m) => notify({ kind: 'error', text: m })}
        />
      )}
    </>
  )
}

function ConsentModal({
  onClose,
  onSaved,
  onError,
}: {
  onClose: () => void
  onSaved: () => void
  onError: (m: string) => void
}) {
  const [f, setF] = useState({
    phone: '',
    consent_type: 'express_written',
    source: '',
    source_url: '',
    disclosure_text: '',
    evidence_ref: '',
  })
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await api.addConsent({
        ...f,
        source_url: f.source_url || null,
        disclosure_text: f.disclosure_text || null,
        evidence_ref: f.evidence_ref || null,
      })
      onSaved()
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Record consent" onClose={onClose} wide>
      <FormGrid>
        <Field label="Phone number">
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} autoFocus />
        </Field>
        <Field label="Type">
          <select
            value={f.consent_type}
            onChange={(e) => setF({ ...f, consent_type: e.target.value })}
          >
            <option value="express_written">Express written — required for marketing to mobiles</option>
            <option value="prior_express">Prior express — number given for this purpose</option>
            <option value="ebr">Established business relationship</option>
            <option value="inquiry">Consumer-initiated inquiry</option>
          </select>
        </Field>
        <Field label="Source" hint="Where it was captured, e.g. web-form, paper, IVR.">
          <input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} />
        </Field>
        <Field label="Source URL" hint="The page the person agreed on, if any.">
          <input value={f.source_url} onChange={(e) => setF({ ...f, source_url: e.target.value })} />
        </Field>
      </FormGrid>

      <Field
        label="Disclosure wording"
        hint="The exact text shown at capture. This is what a dispute turns on."
      >
        <textarea
          rows={3}
          value={f.disclosure_text}
          onChange={(e) => setF({ ...f, disclosure_text: e.target.value })}
        />
      </Field>
      <Field label="Evidence reference" hint="Path or URL to the signed artefact, if held.">
        <input value={f.evidence_ref} onChange={(e) => setF({ ...f, evidence_ref: e.target.value })} />
      </Field>

      <div className="modal-actions">
        <button className="ghost" onClick={onClose}>Cancel</button>
        <button
          className="primary"
          onClick={save}
          disabled={busy || !f.phone.trim() || !f.source.trim()}
        >
          {busy ? 'Saving…' : 'Record'}
        </button>
      </div>
    </Modal>
  )
}

// =========================================================== suppression ====

function Suppression({ role, notify }: { role: Role; notify: Notify }) {
  const [imports, setImports] = useState<DncImport[]>([])
  const [importing, setImporting] = useState(false)
  const [scope, setScope] = useState('federal')
  const [scopeRef, setScopeRef] = useState('')
  const [busy, setBusy] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const load = () => api.dncImports().then(setImports).catch(() => setImports([]))
  useEffect(() => { load() }, [])

  const upload = async (file: File) => {
    setBusy(true)
    try {
      const r = await api.importDnc(file, scope, scopeRef || undefined)
      notify({
        kind: 'success',
        text: `Loaded ${fmtNumber(r.added)} numbers. ${fmtNumber(r.duplicates)} already suppressed, ${fmtNumber(r.rejected)} unreadable.`,
      })
      setImporting(false)
      load()
    } catch (e) {
      notify({ kind: 'error', text: e instanceof ApiError ? e.message : 'Import failed' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Panel
        title="Suppression list imports"
        actions={
          role === 'admin' && (
            <button className="primary" onClick={() => setImporting(true)}>
              Import list
            </button>
          )
        }
      >
        {imports.length === 0 ? (
          <Empty
            message="No suppression lists loaded."
            hint="The federal registry, state lists and litigator lists are loaded here."
          />
        ) : (
          <Table head={['Loaded', 'Scope', 'File', 'Rows', 'Added', 'Duplicates', 'Rejected', '']}>
            {imports.map((i) => (
              <tr key={i.id}>
                <td className="muted">{fmtDateTime(i.created_at)}</td>
                <td>
                  <Badge tone={i.scope === 'litigator' ? 'danger' : 'muted'}>{i.scope}</Badge>
                  {i.scope_ref && <span className="muted small"> {i.scope_ref}</span>}
                </td>
                <td className="mono small">{i.filename ?? i.source}</td>
                <td>{fmtNumber(i.total_rows)}</td>
                <td className="strong">{fmtNumber(i.added)}</td>
                <td className="muted">{fmtNumber(i.duplicates)}</td>
                <td className="muted">{fmtNumber(i.rejected)}</td>
                <td className="row-actions">
                  {role === 'admin' && (
                    <button
                      className="link danger-text"
                      onClick={async () => {
                        if (!confirm(`Undo this import? ${fmtNumber(i.added)} numbers become dialable again.`)) return
                        const r = await api.rollbackDncImport(i.id)
                        notify({ kind: 'success', text: `Removed ${fmtNumber(r.removed)} entries.` })
                        load()
                      }}
                    >
                      Undo
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}

        <p className="muted small">
          Every import is tagged, so loading the wrong file can be undone in one action rather than
          needing a database restore.
        </p>
      </Panel>

      {importing && (
        <Modal title="Import a suppression list" onClose={() => setImporting(false)}>
          <FormGrid>
            <Field label="Scope" hint="What this list binds.">
              <select value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="federal">Federal registry — binds every campaign</option>
                <option value="state">State list</option>
                <option value="litigator">Known litigators — binds everything</option>
                <option value="wireless">Wireless block list</option>
                <option value="internal">Internal suppression</option>
              </select>
            </Field>
            {scope === 'state' && (
              <Field label="State" hint="Two-letter code.">
                <input
                  value={scopeRef}
                  onChange={(e) => setScopeRef(e.target.value.toUpperCase().slice(0, 2))}
                />
              </Field>
            )}
          </FormGrid>

          <div className="dropzone" onClick={() => fileInput.current?.click()}>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept=".txt,.csv,text/plain,text/csv"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            />
            <strong>{busy ? 'Loading…' : 'Choose a file'}</strong>
            <span className="muted small">
              One number per line, or CSV with the number first. Any format — they are normalised.
            </span>
          </div>

          <div className="modal-actions">
            <button className="ghost" onClick={() => setImporting(false)}>Cancel</button>
          </div>
        </Modal>
      )}
    </>
  )
}
